/* eslint-env browser */
import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js'
import { Stripe, StripeElements } from '@stripe/stripe-js'
import Axios from 'axios'
import React from 'react'
import { isEmail } from 'validator'

import { TranslatorProviderInjectedProps } from '../../../client/clientjs/components/types'

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string
  error: boolean
}

// eslint-disable-next-line react/prop-types
const Field: React.FC<FieldProps> = ({ label, id, type, placeholder, autoComplete, value, onChange, error }) => (
  <div className="FormRow">
    <label htmlFor={id} className="FormRowLabel" style={error ? { color: 'red' } : {}}>
      {label}
    </label>
    <input
      style={error ? { color: 'red' } : {}}
      className="FormRowInput"
      id={id}
      type={type}
      placeholder={placeholder}
      required
      autoComplete={autoComplete}
      value={value}
      onChange={onChange}
    />
  </div>
)

const ErrorMessage: React.FC = ({ children }) => <div className="ErrorMessage">{children}</div>

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '13px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
}

interface OwnProps {
  paymentIntent: { paymentIntentClientSecret: string; amount: number; paymentDescription: string }
  stripeInstance: Stripe
  stripeAccount?: string
  paymentEmail: string
  paymentName: string
  elements: StripeElements
  contestId: string
}

interface State {
  loading: boolean
  paymentEmail: string
  paymentName: string
  paymentZipCode: string
  paymentCardComplete: boolean
  paymentExpiryComplete: boolean
  paymentCvcComplete: boolean
  paymentError?: {
    message?: string
  }
}

type Props = OwnProps & TranslatorProviderInjectedProps

export default class RegisterWithPaymentPaymentForm extends React.Component<Props, State> {
  public constructor(props: Props) {
    super(props)
    this.state = {
      paymentEmail: props.paymentEmail,
      paymentName: props.paymentName,
      paymentZipCode: '',
      paymentCardComplete: false,
      paymentExpiryComplete: false,
      paymentCvcComplete: false,
      loading: false,
    }
    this.actuallyPay = this.actuallyPay.bind(this)
  }

  private async actuallyPay(event?: React.FormEvent<HTMLFormElement>) {
    if (event) {
      event.preventDefault()
    }
    this.setState({ loading: true })

    const { elements, stripeInstance, paymentIntent, contestId } = this.props
    const { paymentName, paymentEmail, paymentZipCode } = this.state

    const cardNumber = elements.getElement(CardNumberElement)
    if (!cardNumber) {
      return
    }

    const result = await stripeInstance.confirmCardPayment(paymentIntent.paymentIntentClientSecret, {
      payment_method: {
        card: cardNumber,
        billing_details: {
          email: paymentEmail,
          name: paymentName,
          address: {
            postal_code: paymentZipCode,
          },
        },
      },
    })

    if (result.error) {
      this.setState({ paymentError: result.error })
    } else if (result.paymentIntent) {
      const resultConfirm = (
        await Axios.post<any>(`/api/contests/${contestId}/confirmStripePaymentIntent`, {
          withCredentials: true,
          paymentIntentId: result.paymentIntent.id,
          contestId,
        })
      ).data
      window.location.reload()
    }
  }

  public render() {
    const { paymentIntent, translate } = this.props
    const {
      paymentEmail,
      paymentName,
      paymentCardComplete,
      paymentCvcComplete,
      paymentExpiryComplete,
      paymentZipCode,
      paymentError,
      loading,
    } = this.state

    return (
      <div className="columns is-multiline is-centered has-text-centered payment-form">
        <div
          className="column is-12 has-text-centered"
          style={{
            overflowY: 'scroll',
            maxWidth: '100%',
            width: 600,
            border: '1px solid #e3e8ee',
            borderRadius: 4,
            backgroundColor: '#f7fafc',
          }}
        >
          <p style={{ fontWeight: 'bold', marginBottom: 16 }}>
            {`${paymentIntent.paymentDescription} : ${paymentIntent.amount / 100}€`}
          </p>
          <form className="Form" onSubmit={this.actuallyPay}>
            <fieldset className="FormGroup">
              <Field
                label={translate('registerPayment.checkoutForm.name')}
                id="name"
                type="text"
                required
                autoComplete="name"
                value={paymentName}
                error={(paymentName || '').trim().length === 0}
                onChange={event => {
                  this.setState({ paymentName: event.target.value })
                }}
              />
              <Field
                label={translate('registerPayment.checkoutForm.email')}
                id="email"
                type="email"
                required
                autoComplete="email"
                value={paymentEmail}
                error={(paymentEmail || '').trim().length === 0 || !isEmail(paymentEmail)}
                onChange={event => {
                  this.setState({ paymentEmail: event.target.value })
                }}
              />
              <Field
                label={translate('registerPayment.checkoutForm.zip')}
                id="zip"
                type="text"
                required
                value={paymentZipCode}
                error={false}
                onChange={event => {
                  this.setState({ paymentZipCode: event.target.value })
                }}
              />
            </fieldset>
            <fieldset className="FormGroup">
              <p style={{ marginBottom: 8 }}>{translate('registerPayment.checkoutForm.cardDetails')}</p>
              <label className="FormRowLabel" htmlFor="number">
                {translate('registerPayment.checkoutForm.cardNumber')}
              </label>
              <CardNumberElement
                id="number"
                options={CARD_ELEMENT_OPTIONS}
                onChange={event => {
                  this.setState({
                    paymentError: event.error,
                    paymentCardComplete: event.complete,
                  })
                }}
              />
              <label className="FormRowLabel" htmlFor="expiry">
                {translate('registerPayment.checkoutForm.expiration')}
              </label>
              <CardExpiryElement
                id="expiry"
                options={CARD_ELEMENT_OPTIONS}
                onChange={event => {
                  this.setState({
                    paymentError: event.error,
                    paymentExpiryComplete: event.complete,
                  })
                }}
              />
              <label className="FormRowLabel" htmlFor="cvc">
                {translate('registerPayment.checkoutForm.cvc')}
              </label>
              <CardCvcElement
                id="cvc"
                options={CARD_ELEMENT_OPTIONS}
                onChange={event => {
                  this.setState({
                    paymentError: event.error,
                    paymentCvcComplete: event.complete,
                  })
                }}
              />
            </fieldset>
          </form>
        </div>
        {paymentError && (
          <div className="column is-12 has-text-centered">
            <ErrorMessage>{paymentError.message}</ErrorMessage>
          </div>
        )}
        <div className="column is-12 has-text-centered">
          <button
            type="submit"
            className={`button is-call-to-action ${loading ? 'is-loading' : ''}`}
            disabled={
              loading ||
              !paymentCardComplete ||
              !paymentExpiryComplete ||
              !paymentCvcComplete ||
              (paymentZipCode || '').trim().length === 0 ||
              (paymentName || '').trim().length === 0 ||
              (paymentEmail || '').trim().length === 0
            }
            onClick={() => this.actuallyPay()}
          >
            {translate('registerPayment.button.confirmPayment', { amount: `${paymentIntent.amount / 100}€` })}
          </button>
        </div>
      </div>
    )
  }
}
